import { Check, ChevronDown, Folder, Loader2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/atoms/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/atoms/popover';
import { useUpdateProject } from '@/features/projects/services/project.api';
import { WORKSPACE_PICKER_SURFACE } from '@/features/workspaces/constants/workspacePicker';
import { useAllWorkspacesQuery } from '@/features/workspaces/services/workspace.api';
import { filterWorkspacesByName } from '@/features/workspaces/utils/filterWorkspaces';
import { useMixpanel } from '@/hooks/useMixpanel';
import { ACTION_TRACKED } from '@/libs/mixpanel';
import { cn } from '@/libs/utils/cn';
import { type IProject, PROJECT_STATUS } from '@/types/api/IProject';

interface WorkspaceCellProps {
  project: IProject;
  /** Current History-table page — needed by the optimistic list-cache write. */
  page: number;
}

/**
 * Inline WORKSPACE-column editor for the History (Export) table.
 *
 * - Assigned → a chip labelled with `workspace_name`.
 * - Unassigned → a clickable "Assign workspace" placeholder with a
 *   select-style ring and chevron.
 * - `pending`/`running` → non-interactive: rendered as a disabled-looking label
 *   carrying a `title`/`aria-label` explaining why it can't change.
 *   (`cancelled`/`failed` are terminal, not busy, so they remain editable.)
 * - While the assign/move PATCH is in flight the trigger shows a spinner, is
 *   marked `aria-busy`, and cannot be re-opened until the mutation settles; the
 *   optimistic chip flip still happens immediately.
 * - Clicking opens a searchable workspace picker (Radix Popover + `cmdk`
 *   Command) filtered with `filterWorkspacesByName`; the project's current
 *   workspace is marked with a check + `data-current`.
 * - Selecting a workspace assigns/moves the project via `useUpdateProject`
 *   (optimistic + revert-on-error) and fires `ACTION_TRACKED.PROJECT_UPDATED`.
 *   There is NO detach option — assign/move only.
 * - All pointer/keyboard interaction on the cell stops propagating so it never
 *   triggers the row's project-navigation.
 */
export function WorkspaceCell({ project, page }: WorkspaceCellProps) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');

  const { data: listData } = useAllWorkspacesQuery();
  const updateProject = useUpdateProject();
  const trackAction = useMixpanel((state) => state.trackAction);

  // Busy = the project is mid-computation, so its workspace must stay frozen.
  // The status type exposes pending/running/completed/cancelled/failed; only
  // pending and running are in-flight. (The backend also rejects `cancelling`,
  // but no such status exists in the type, so there is nothing to guard here.)
  const isBusy =
    project.status === PROJECT_STATUS.PENDING || project.status === PROJECT_STATUS.RUNNING;

  // Pending PATCH affordance: spinner + aria-busy on the trigger, and the picker
  // can't be re-opened until the mutation settles.
  const isPending = updateProject.isPending;

  const allWorkspaces = listData?.workspaces ?? [];
  const filtered = useMemo(
    () => filterWorkspacesByName(allWorkspaces, filter),
    [allWorkspaces, filter],
  );

  // Stop cell interactions from reaching the row's navigation handler.
  function stop(event: React.SyntheticEvent) {
    event.stopPropagation();
  }

  // Block only the keys that would activate the surrounding row; let Escape
  // and arrow keys reach the Popover + cmdk Command so they keep working
  // and never trap keyboard focus (WCAG 2.1.2).
  function stopRowKeys(event: React.KeyboardEvent) {
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
      event.stopPropagation();
    }
  }

  async function handleSelect(workspaceId: string) {
    setOpen(false);
    if (workspaceId === project.workspace_id) return;

    const workspaceName = allWorkspaces.find((ws) => ws.id === workspaceId)?.name ?? null;
    const patch = { workspace_id: workspaceId, workspace_name: workspaceName };

    try {
      await updateProject.mutateAsync({
        projectId: project.id,
        updatedProject: patch,
        page,
      });
      trackAction(ACTION_TRACKED.PROJECT_UPDATED, {
        success: true,
        project_id: project.id,
        // Derive from the patch keys (matches ProjectsList) so the reported
        // fields stay in sync with what the request actually carried.
        updated_fields: Object.keys(patch),
      });
    } catch (error) {
      // The hook already reverted the optimistic cache write and toasted the
      // error; record the failed attempt for analytics parity with the modal.
      trackAction(ACTION_TRACKED.PROJECT_UPDATED, {
        success: false,
        project_id: project.id,
        error_message: error instanceof Error ? error.message : 'Unknown error',
      });
    }
  }

  // Busy projects render a static, non-interactive label. It must read
  // as "temporarily unavailable" (dimmed + not-allowed cursor) and expose WHY to
  // assistive tech via title + aria-label, distinct from the clickable `—`.
  if (isBusy) {
    const busyReason = "Workspace can't be changed while the project is running.";
    return (
      <span
        role="note"
        title={busyReason}
        aria-label={busyReason}
        className="inline-flex w-[170px] cursor-not-allowed items-center gap-1.5 rounded-md px-2 py-1.5 -my-1.5 text-sm text-muted-foreground opacity-60"
      >
        <Folder className="h-3 w-3 shrink-0" aria-hidden="true" />
        <span className="min-w-0 flex-1 truncate">{project.workspace_name ?? '—'}</span>
      </span>
    );
  }

  const triggerLabel = project.workspace_name
    ? `Workspace: ${project.workspace_name}`
    : 'Assign a workspace';

  // While the PATCH is in flight, keep the picker shut and block re-opening it.
  function handleOpenChange(next: boolean) {
    if (isPending) return;
    setOpen(next);
  }

  const hasNoWorkspaces = allWorkspaces.length === 0;

  return (
    <div className="flex w-full" onClick={stop} onKeyDown={stopRowKeys} role="none">
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={triggerLabel}
            aria-busy={isPending}
            disabled={isPending}
            className={cn(
              'inline-flex w-[170px] items-center gap-1.5 rounded-md px-2 py-1.5 -my-1.5 text-left text-sm outline-none transition-colors',
              'bg-background ring-1 ring-inset ring-border hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring',
              project.workspace_name ? 'text-foreground' : 'text-muted-foreground',
              isPending && 'opacity-60',
            )}
          >
            {isPending ? (
              <Loader2 className="h-3 w-3 shrink-0 animate-spin" aria-hidden="true" />
            ) : (
              <Folder className="h-3 w-3 shrink-0 text-muted-foreground" aria-hidden="true" />
            )}
            <span className="min-w-0 flex-1 truncate" title={project.workspace_name ?? undefined}>
              {project.workspace_name ?? 'Assign workspace'}
            </span>
            <ChevronDown className="h-3 w-3 shrink-0" aria-hidden="true" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          className={cn('w-64 p-0', WORKSPACE_PICKER_SURFACE)}
          align="start"
          onClick={stop}
          onKeyDown={stopRowKeys}
        >
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Filter workspaces…"
              value={filter}
              onValueChange={setFilter}
            />
            <CommandList>
              <CommandEmpty>
                {hasNoWorkspaces
                  ? 'No workspaces yet. Create one from the Map page.'
                  : 'No workspaces found.'}
              </CommandEmpty>
              {filtered.map((ws) => {
                const isCurrent = ws.id === project.workspace_id;
                return (
                  <CommandItem
                    key={ws.id}
                    value={ws.id}
                    data-current={isCurrent ? 'true' : undefined}
                    aria-current={isCurrent ? 'true' : undefined}
                    onSelect={handleSelect}
                  >
                    <Check
                      className={cn('h-4 w-4 shrink-0', isCurrent ? 'text-primary' : 'invisible')}
                      aria-hidden="true"
                    />
                    <span className="min-w-0 flex-1 truncate" title={ws.name}>
                      {ws.name}
                    </span>
                  </CommandItem>
                );
              })}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
